import { type ReactNode, useCallback, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Loader2, Menu } from 'lucide-react'
import Sidebar from './components/Sidebar'
import { SIDEBAR_DRAWER_ID } from './components/sidebarConfig'
// QBO Connect/Disconnect + legal pages — registered with Intuit as this
// app's exact Launch/Disconnect URLs and legal-page links (submitted app
// config), so they must exist at these literal paths: /qbo/connect,
// /qbo/disconnected, /legal/eula, /legal/privacy. They render OUTSIDE the
// auth wall below (no AuthProvider/AuthGate/AppShell) — Intuit's reviewers
// and a signed-out browser mid-OAuth-handshake must be able to load them
// without a ClickPay session.
import QboConnect from './pages/QboConnect'
import QboConnected from './pages/QboConnected'
import QboDisconnected from './pages/QboDisconnected'
import LegalEula from './pages/LegalEula'
import LegalPrivacy from './pages/LegalPrivacy'
// ClickPay login UI — AuthProvider resolves /api/auth/me on boot; AuthGate
// below swaps the whole shell for the Login page when enforcement is on and
// no session exists. In 'open' mode (CLICKPAY_AUTH=off) everything renders
// exactly as before, with no login affordance anywhere.
import { AuthProvider, useAuth } from './lib/auth'
import { AmcStatusProvider } from './lib/useAmcStatus'
// ClickPay v2 design system — theme tokens live in index.css; the provider
// manages the .dark class + persistence, the toggle sits in the header.
import { ThemeProvider } from './lib/theme'
// Round-4 Area D (POWER): per-user table column-layout + saved-views store.
// Mounted inside AuthProvider so it hydrates from /api/table-layout for the
// signed-in user (localStorage fallback when auth is off / offline).
import { TableLayoutProvider } from './lib/tableLayout'
import ThemeToggle from './components/ThemeToggle'
import LoginPage from './pages/Login'
import UserChip from './components/UserChip'
import FeedbackButton from './components/FeedbackButton'
// TRACK D · F4.5 (D5.4): the top-bar scope control. The old "All Marketplaces"
// ProfileSwitcher (a decorative multi-marketplace multi-select read by only 2
// pages) is replaced by the Operator switcher — My products / Whole catalog /
// <teammate> — which scopes the app to a book of products. Marketplace
// narrowing moved to the per-page Marketplace dropdown (F4.3). ProfileSwitcher's
// module stays in the tree because its useActiveProfile/useActiveProfiles hooks
// are still consumed by Campaigns / ProductAds; only the header chip is swapped.
import OperatorSwitcher from './components/OperatorSwitcher'
// SellerMate W6.3: docked AI side panel (prompt library, scheduled briefings,
// grid context chips) + its header button. The panel is mounted once below.
import AskAiButton from './components/ai/AskAiButton'
import AiSidePanel from './components/ai/AiSidePanel'
import UpdateAvailablePill from './components/UpdateAvailablePill'
// PR 8 (plan D1): ONE bell replaces the three attention surfaces the header
// carried — the "Notifications 75" link (NotificationsBell), the business
// Alerts pill beside it, and the "Needs attention" health pill
// (SystemHealthControl). The bell's dropdown holds For you / Data / System;
// nothing underlying is gone: its Data footer keeps the PPC refresh chip for
// everyone and admins get the health pill's Systems check rows (BackendStatus
// with Start/Restart, FreshnessBadge, PpcFreshnessChip, SystemStatusDot)
// under System — see HeaderBell's docblock. The old component files stay in
// the tree, unmounted, until a follow-up removes them.
import HeaderBell from './components/notifications/HeaderBell'
import PendingTray from './components/PendingTray'
// SellerMate W0.2: Ctrl+K command palette — pages + records + "Ask AI".
// Mounted once; it owns its own header button and the global hotkey.
import CommandPalette from './components/CommandPalette'
// SellerMate W0.3: the app-wide date range ("current ▸ compared to"). Shows
// only while a page that uses it (lib/dateRangeContext.tsx) is on screen.
import AppDateRangeControl from './components/AppDateRangeControl'
import AppDateRangeUrlSync from './components/AppDateRangeUrlSync'
// Wave 3 · B7 — per-record keep-alive bottom tabs (GoFlow-style). The routed
// page tree itself now lives in AppRoutes.tsx (a pure extraction — see its
// docblock) so TabsHost can render one instance per open tab, each matching
// against that tab's own remembered location instead of a single shared one.
// TabsProvider owns the open-tab list (persisted to localStorage, mirroring
// lib/tableLayout.tsx's pattern); TabBar is the fixed bottom pill strip.
import { TabsProvider } from './tabs/TabsContext'
import { UIPrefsProvider } from './ops/lib/uiPrefs'
import TabsHost from './tabs/TabsHost'
import TabBar from './tabs/TabBar'
import { ProductQuickViewProvider } from './components/productQuickView/ProductQuickViewProvider'
// UI overhaul PR-2: the one data cache. Above the router and every pane so
// two components (or two tabs) asking for the same thing share one request
// and one copy — see lib/query/queryClient.ts.
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/query/queryClient'

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ThemeProvider>
        <Routes>
          {/* Public, un-authed pages — see the Qbo/Legal import comment
              above for why these sit outside the auth wall. */}
          <Route path="/qbo/connect" element={<QboConnect />} />
          {/* Not Intuit-registered — ClickPay's own OAuth callback lands here
              on success (see QboConnected.tsx docblock). */}
          <Route path="/qbo/connected" element={<QboConnected />} />
          <Route path="/qbo/disconnected" element={<QboDisconnected />} />
          <Route path="/legal/eula" element={<LegalEula />} />
          <Route path="/legal/privacy" element={<LegalPrivacy />} />
          <Route path="*" element={<AuthedApp />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
    </QueryClientProvider>
  )
}

/** Everything that requires a ClickPay session — the real app, gated by
 *  AuthGate. Mounted at the catch-all so every route not claimed by the
 *  public pages above falls through to the normal authed route tree
 *  (AppRoutes.tsx, rendered inside AppShell). */
function AuthedApp() {
  return (
    <AuthProvider>
      <TableLayoutProvider>
        <AmcStatusProvider>
          <AuthGate>
            <AppShell />
          </AuthGate>
        </AmcStatusProvider>
      </TableLayoutProvider>
    </AuthProvider>
  )
}

/** Boot gate: splash while /api/auth/me resolves; Login page (in place —
 *  URL preserved, so login lands back on the requested route) when auth is
 *  enforced without a session; otherwise the app. */
function AuthGate({ children }: { children: ReactNode }) {
  const { mode } = useAuth()
  if (mode === 'loading') {
    return (
      <div className="h-screen bg-canvas flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-ink-faint" aria-label="Loading" />
      </div>
    )
  }
  if (mode === 'login') return <LoginPage />
  return <>{children}</>
}

function AppShell() {
  // W0.5: below `md` the sidebar is an off-canvas drawer; the hamburger in
  // the header opens it and the sidebar closes it (a link, the scrim, the X).
  const [navOpen, setNavOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeNav = useCallback(() => setNavOpen(false), [])
  return (
    // PR 12 (D7): ONE display-preferences store for the whole shell, so the
    // Settings › Display choices reach main-app and ops tables alike (it used
    // to be mounted only under OpsLayout, and again in EntityTableAdapter).
    <UIPrefsProvider>
      <TabsProvider>
        {/* overflow-x-clip: nothing in the shell may widen the page past the
            viewport (a dropdown hanging off a 375px phone would otherwise
            give the whole app a sideways scroll). Clip, not hidden, so no
            scroll container is created and vertical overflow is untouched. */}
        <div className="flex flex-col h-screen bg-canvas text-ink overflow-x-clip">
          {/* Header bar — Aakaar parity: profile switcher in top-right. On a
              phone the controls wrap onto a second row instead of pushing
              the page sideways. */}
          <header className="min-h-12 md:h-12 bg-surface border-b border-edge flex flex-wrap md:flex-nowrap items-center justify-end gap-x-2 gap-y-1 md:gap-3 px-2 py-1 md:px-4 md:py-0 shrink-0">
            {/* Phone only. No mr-auto of its own: the search box after it
                carries the one mr-auto, so the two sit together on the left
                and the controls stay on the right. */}
            <button
              ref={menuButtonRef}
              onClick={() => setNavOpen(true)}
              className="md:hidden p-1.5 rounded-md text-ink-muted hover:text-ink hover:bg-surface-3 transition-colors duration-150"
              aria-label="Open menu"
              aria-expanded={navOpen}
              aria-controls={navOpen ? SIDEBAR_DRAWER_ID : undefined}
              title="Menu"
            >
              <Menu size={18} />
            </button>
            <CommandPalette />
            <AppDateRangeControl />
            {/* PR 12 (C10): the range travels in the URL (?from&to&cmp). */}
            <AppDateRangeUrlSync />
            {/* Mounted once, app-wide — see useVersionSkew.ts. Renders
                nothing while the tab's bundle matches the live backend. This
                is deliberately SEPARATE from the bell below: a stale bundle
                is not a system fault, it's a "you are looking at old code"
                notice, and folding it into the bell would make a healthy
                system read as a problem. */}
            <UpdateAvailablePill />
            <AskAiButton showUnread />
            {/* PR 8: the one bell — system, business and data notifications,
                with the old health pill's rows inside it for admins. */}
            <HeaderBell />
            {/* Phase 3 (2026-06-01): pending-tray lives in the header so it's
                available on every analytics tab; labelled "Pending changes"
                since PR 8 (it was an unlabelled count). The Pick-Operator
                picker was removed 2026-06 — the logged-in user IS the
                operator (UserChip shows the identity; the backend derives
                edit attribution from the session). */}
            <PendingTray />
            <OperatorSwitcher />
            <div className="hidden md:block h-5 w-px bg-edge" aria-hidden />
            {/* Phase 4 #17: in-app feedback on every route (analytics + ops,
                which render under this same shell). Present in all modes. */}
            <FeedbackButton />
            <ThemeToggle />
            {/* ClickPay: authenticated identity + logout. Renders nothing
                when CLICKPAY_AUTH=off (open mode) — header unchanged. */}
            <UserChip />
          </header>
          <div className="flex flex-1 overflow-hidden">
            <Sidebar mobileOpen={navOpen} onMobileClose={closeNav} returnFocusTo={menuButtonRef} />
            {/* UI overhaul PR-1a: <main> no longer scrolls — each tab pane is
                its own scroll container (TabsHost), so pages stop sharing one
                scrollbar and remember their own offset. */}
            <main className="flex-1 overflow-hidden relative">
              {/* PR 10 (plan I5): the app-wide product quick view — any
                  product thumbnail on any tab opens it (PPC and ops pages
                  alike); the drawer portals to <body>. */}
              <ProductQuickViewProvider>
                <TabsHost />
              </ProductQuickViewProvider>
            </main>
          </div>
          {/* The tab bar is the last row of the column, in normal flow: the
              sidebar and the content end where it begins, so nothing hides
              under it and nothing needs a spacer. Renders null with one tab. */}
          <TabBar />
          <AiSidePanel />
        </div>
      </TabsProvider>
    </UIPrefsProvider>
  )
}
